import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { handleAdminGetBlogs, handleAdminDeleteBlog } from '../../services/APIClient';

const findBlogList = (value, depth = 0) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== 'object' || depth > 6) return [];

  const preferredKeys = ['blogList', 'blogs', 'items', 'results', 'docs'];
  for (const key of preferredKeys) {
    if (Array.isArray(value[key])) return value[key];
  }

  const envelopeKeys = ['data', 'response', 'result', 'payload'];
  for (const key of envelopeKeys) {
    const nested = findBlogList(value[key], depth + 1);
    if (nested.length) return nested;
  }

  return [];
};



const Blogs = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');


  const fetchBlogs = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await handleAdminGetBlogs({ pageNo: 1, limit: 100 });
      const rawList = findBlogList(response);

      if (!rawList.length && response?.code !== 200) {
        throw new Error('The blog list could not be loaded');
      }


      const normalized = rawList.map((b) => ({
        id: b._id || b.id,
        title: b.title,
        // Normalize status to have initial capital (Published/Draft)
        status:
          typeof b.status === 'string'
            ? b.status.charAt(0).toUpperCase() + b.status.slice(1)
            : b.status,
        views: b.views ?? 0,
        // format date for display
        date: b.createdAt ? new Date(b.createdAt).toLocaleDateString() : b.date || '',
        // keep original object for any other properties
        ...b,
      }));


      setBlogs(normalized);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      setError('Your published blogs are safe, but the list could not be loaded. Please refresh and try again.');
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchBlogs();
  }, []);


  const deleteBlog = async (id) => {
    if (window.confirm('Are you sure you want to delete this blog?')) {
      try {
        const response = await handleAdminDeleteBlog(id);
        // handleAdminDeleteBlog returns the API response body (may vary by backend)
        const ok =
          response?.code === 200 ||
          response?.status === 200 ||
          response?.data?.status === 200 ||
          response?.data?.code === 200 ||
          response?.success === true ||
          // if no structured response but no error thrown, assume success
          !!response;


        if (ok) {
          // simplest: refresh list
          fetchBlogs();
        } else {
          alert('Failed to delete blog');
        }
      } catch (error) {
        console.error('Error deleting blog:', error);
        alert('Error deleting blog');
      }
    }
  };
  return (
    <div className="min-h-screen bg-[#07090D] p-4 text-white sm:p-6 lg:p-8">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white">Blog Management</h1>
          <p className="text-white/50 mt-1">Create and manage your blog posts</p>
        </div>
        


        <div className="mt-4 md:mt-0">
          <button
            onClick={() => navigate('/add-blog')}
            className="bg-[#67A3FA] hover:bg-[#82B5FC] text-[#07090D] px-5 py-2.5 rounded-lg font-semibold flex items-center gap-2 shadow-[0_10px_30px_rgba(103,163,250,0.2)] transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path>
            </svg>
            Add New Blog
          </button>
        </div>
      </div>


      {/* Blogs Table / Mobile list */}
      <div className="bg-[#0D1017] rounded-2xl shadow-[0_18px_55px_rgba(0,0,0,0.28)] border border-white/10 overflow-hidden">
        {/* Mobile: stacked cards (visible on small screens) */}
        <div className="p-4 md:hidden">
          {blogs.map((blog) => (
            <div key={blog.id} className="mb-4 bg-white/[0.04] border border-white/10 rounded-xl p-4 shadow-sm">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="text-sm font-medium text-white">{blog.title}</div>
                  <div className="text-xs text-white/45 mt-1">{blog.date}</div>
                </div>
                <div className="ml-3 text-right">
                  <span className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    (String(blog.status || '').toLowerCase() === 'published')
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {blog.status}
                  </span>
                </div>
              </div>


              <div className="mt-3 flex items-center justify-between">
                <div className="text-sm text-white/50 flex items-center gap-2">
                  <svg className="w-4 h-4 text-white/35" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                  </svg>
                  {blog.views}
                </div>


                <div className="flex items-center gap-2">
                  <button
                    onClick={() => navigate(`/edit-blog/${blog.id}`)}
                    className="text-[#67A3FA] hover:text-[#9FC8FF] flex items-center gap-1 text-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => deleteBlog(blog.id)}
                    className="text-red-300 hover:text-red-200 flex items-center gap-1 text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>


        {/* Desktop: table (hidden on small screens) */}
        <div className="overflow-x-auto hidden md:block">
          <table className="min-w-full divide-y divide-white/10">
            <thead className="bg-white/[0.04]">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white/45 uppercase tracking-wider">
                  Title
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white/45 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white/45 uppercase tracking-wider">
                  Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white/45 uppercase tracking-wider">
                  Views
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white/45 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-transparent divide-y divide-white/10">
              {blogs.map((blog) => (
                <tr key={blog.id} className="hover:bg-white/[0.035] transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="ml-4">
                        <div className="text-sm font-medium text-white">
                          {blog.title}
                        </div>
                       
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      (String(blog.status || '').toLowerCase() === 'published')
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                      }`}>
                      {blog.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white/50">
                    {blog.date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white/50">
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4 text-white/35" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                      </svg>
                      {blog.views}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => navigate(`/edit-blog/${blog.id}`)}
                        className="text-[#67A3FA] hover:text-[#9FC8FF] flex items-center gap-1"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                        </svg>
                        Edit
                      </button>
                      <button
                        onClick={() => deleteBlog(blog.id)}
                        className="text-red-300 hover:text-red-200 flex items-center gap-1"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                        </svg>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>


        {loading && (
          <div className="px-6 py-12 text-center text-sm text-white/50">Loading blogs…</div>
        )}

        {error && (
          <div className="m-5 rounded-xl border border-red-400/20 bg-red-400/10 px-4 py-3 text-sm text-red-200">
            {error}
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && blogs.length === 0 && (
          <div className="text-center py-12">
            <div className="text-white/30 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
            </div>
            <h3 className="text-lg font-medium text-white mb-2">No blogs yet</h3>
            <p className="text-white/50 mb-6">Get started by creating your first blog post.</p>
            <button
              onClick={() => navigate('/add-blog')}
              className="bg-[#67A3FA] hover:bg-[#82B5FC] text-[#07090D] px-6 py-2.5 rounded-lg font-semibold"
            >
              Create Your First Blog
            </button>
          </div>
        )}
      </div>


      {/* Stats Cards */}
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-[#0D1017] p-5 rounded-xl shadow-[0_12px_35px_rgba(0,0,0,0.2)] border border-white/10">
          <div className="flex items-center">
            <div className="p-3 bg-[#67A3FA]/15 rounded-lg mr-4">
              <svg className="w-6 h-6 text-[#67A3FA]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
            </div>
            <div>
              <p className="text-sm text-white/50">Total Blogs</p>
              <p className="text-2xl font-bold">{blogs.length}</p>
            </div>
          </div>
        </div>


        <div className="bg-[#0D1017] p-5 rounded-xl shadow-[0_12px_35px_rgba(0,0,0,0.2)] border border-white/10">
          <div className="flex items-center">
            <div className="p-3 bg-emerald-400/15 rounded-lg mr-4">
              <svg className="w-6 h-6 text-emerald-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
                <div>
                  <p className="text-sm text-white/50">Published</p>
                  <p className="text-2xl font-bold text-emerald-300">
                    {blogs.filter(b => String(b.status || '').toLowerCase() === 'published').length}
                  </p>
                </div>
          </div>
        </div>


        <div className="bg-[#0D1017] p-5 rounded-xl shadow-[0_12px_35px_rgba(0,0,0,0.2)] border border-white/10">
          <div className="flex items-center">
            <div className="p-3 bg-amber-400/15 rounded-lg mr-4">
              <svg className="w-6 h-6 text-amber-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
              </svg>
            </div>
                <div>
                  <p className="text-sm text-white/50">Drafts</p>
                  <p className="text-2xl font-bold text-amber-300">
                    {blogs.filter(b => String(b.status || '').toLowerCase() === 'draft').length}
                  </p>
                </div>
          </div>
        </div>


        <div className="bg-[#0D1017] p-5 rounded-xl shadow-[0_12px_35px_rgba(0,0,0,0.2)] border border-white/10">
          <div className="flex items-center">
            <div className="p-3 bg-violet-400/15 rounded-lg mr-4">
              <svg className="w-6 h-6 text-violet-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
              </svg>
            </div>
            <div>
              <p className="text-sm text-white/50">Total Views</p>
              <p className="text-2xl font-bold text-violet-300">
                {blogs.reduce((sum, blog) => sum + blog.views, 0)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


export default Blogs;


